import React, { useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import AppLayout from "./components/layout/AppLayout";
import DashboardPage from "./pages/DashboardPage";
import ScanPage from "./pages/ScanPage";
import ResultsPage from "./pages/ResultsPage";
import UVIndexPage from "./pages/UVIndexPage";
import ProfilePage from "./pages/ProfilePage";
import LoginPage from "./pages/LoginPage";
import OAuthCallbackPage from "./pages/OAuthCallbackPage";
import { mockScanSamples } from "./mock/scans";
import { AnalysisResult } from "./services/api";
import { useAuth } from "./context/AuthContext";

type SampleKey = keyof typeof mockScanSamples;

const HomeRedirect: React.FC = () => {
  const location = useLocation();
  // Check if we have auth tokens in the URL hash
  if (location.hash && (location.hash.includes("access_token=") || location.hash.includes("id_token="))) {
    return <Navigate to={`/oauth/callback${location.hash}`} replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

const App: React.FC = () => {
  const { isReady } = useAuth();
  const [sampleId, setSampleId] = useState<SampleKey>("handClear");
  const [photoUri, setPhotoUri] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(
    null
  );

  const handleSampleChange = (id: string) => {
    // Check if the id is a valid SampleKey, otherwise default or handle error
    if (id in mockScanSamples) {
      setSampleId(id as SampleKey);
    } else {
      console.warn(`Invalid sample ID: ${id}`);
    }
    setPhotoUri(null);
    setPhotoFile(null);
    setAnalysisResult(null);
  };

  const handleCapture = (uri: string | null, file: File | null = null) => {
    setPhotoUri(uri);
    setPhotoFile(file);
  };

  if (!isReady) {
    return <div style={{ padding: 24 }}>Loading session...</div>;
  }

  return (
    <BrowserRouter>
      <AppLayout>
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route
            path="/dashboard"
            element={
              <DashboardPage
                onStartScan={() => handleSampleChange("handClear")}
              />
            }
          />
          <Route
            path="/scan"
            element={
              <ScanPage
                sampleId={sampleId}
                photoUri={photoUri}
                onCapture={handleCapture}
                photoFile={photoFile}
                onAnalysisComplete={setAnalysisResult}
              />
            }
          />
          <Route
            path="/results"
            element={
              <ResultsPage
                sampleId={sampleId}
                photoUri={photoUri}
                analysisResult={analysisResult}
              />
            }
          />
          <Route path="/uv-index" element={<UVIndexPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
          <Route path="/oauth/callback/" element={<OAuthCallbackPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AppLayout>
    </BrowserRouter>
  );
};

export default App;
